import { useEffect, useMemo, useRef, useState } from "react";
import { Play, Pause, SkipBack, SkipForward, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import flyerAsset from "@/assets/ndlcs-services-flyer.png.asset.json";

/**
 * A 5-minute in-app explainer for the radio page.
 * 10 slides × 30s = 300s. Auto-advances, pauseable, keyboard-nav.
 *
 * Two stylised SVG hosts ("Kabelo" and "Naledi") sit at the bottom of the
 * stage and "talk" while their slide is on. All animation is pure CSS so
 * it works in every browser without JS timing loops.
 */

type Slide = {
  id: string;
  eyebrow: string;
  title: string;
  points: string[];
  host: "kabelo" | "naledi" | "both";
  accent?: "navy" | "gold" | "cream";
  showFlyer?: boolean;
};

const SLIDE_MS = 30_000;

const SLIDES: Slide[] = [
  {
    id: "intro",
    eyebrow: "On air · 5 minutes",
    title: "Welcome to Nkadimeng Danny Legal & Compliance Support",
    points: [
      "Empowering businesses. Ensuring compliance. Delivering peace of mind.",
      "Reg No. 2026/518678/07 — a South African legal SUPPORT and compliance consultancy.",
      "In the next 5 minutes, meet the services we offer and how we help.",
    ],
    host: "both",
    accent: "navy",
    showFlyer: true,
  },
  {
    id: "who",
    eyebrow: "Who we are",
    title: "A compliance-first partner, not a law firm",
    points: [
      "Founded by an LLB graduate (UNISA); we are not an attorneys' firm.",
      "We work alongside — or refer to — admitted practitioners when the law reserves the work for them.",
      "Everything we do is confidential and POPIA-aligned.",
    ],
    host: "kabelo",
  },
  {
    id: "legal",
    eyebrow: "Service 1",
    title: "Legal Support",
    points: [
      "Legal research and document drafting",
      "Contract drafting & review",
      "Affidavits, sworn statements, demand letters",
      "Legal correspondence and litigation support",
    ],
    host: "naledi",
    accent: "gold",
  },
  {
    id: "compliance",
    eyebrow: "Service 2",
    title: "Compliance Services",
    points: [
      "Compliance audits and regulatory compliance",
      "Risk & compliance management",
      "Policy development and monitoring",
      "Internal compliance reporting",
    ],
    host: "kabelo",
  },
  {
    id: "contracts",
    eyebrow: "Service 3",
    title: "Contract & Commercial Support",
    points: [
      "Service Level Agreements (SLAs)",
      "Non-Disclosure Agreements (NDAs)",
      "Memoranda of Understanding, partnership & supplier agreements",
      "Commercial contract review",
    ],
    host: "naledi",
    accent: "gold",
  },
  {
    id: "labour",
    eyebrow: "Service 4",
    title: "Labour Law & Employment Support",
    points: [
      "Disciplinary hearing assistance and CCMA document preparation",
      "Employment contracts and workplace policies",
      "Misconduct investigations",
      "Labour compliance support for employers and employees",
    ],
    host: "kabelo",
  },
  {
    id: "business",
    eyebrow: "Service 5",
    title: "Business & Corporate Support",
    points: [
      "Company registration and annual returns",
      "CIPC services",
      "Board & shareholder resolutions",
      "Governance documentation",
    ],
    host: "naledi",
  },
  {
    id: "training",
    eyebrow: "Service 6",
    title: "Training & Advisory",
    points: [
      "Compliance training and labour law awareness",
      "POPIA awareness workshops",
      "Corporate governance workshops",
      "Ethics & compliance training",
    ],
    host: "kabelo",
    accent: "gold",
  },
  {
    id: "why",
    eyebrow: "Why choose us",
    title: "Professional. Compliance-focused. Client-centred.",
    points: [
      "Solutions that drive growth — priced for South African businesses",
      "Direct access to your consultant via portal, WhatsApp and email",
      "Every document validated against our zero-mistake quality checks",
      "Your compliance partner. Your business protector.",
    ],
    host: "both",
    accent: "navy",
  },
  {
    id: "cta",
    eyebrow: "Get in touch",
    title: "Ready when you are",
    points: [
      "📞  071 276 6198",
      "✉️  nkadimengdanny14@gmail.com",
      "📍  623 Mpheleng Cafe Street, Dennilton, Groblersdal, Limpopo 1030",
      "Book a consultation on the website — virtual or in-person, across SA.",
    ],
    host: "both",
    accent: "gold",
    showFlyer: true,
  },
];

export function ServicesExplainerSlides({ onClose }: { onClose?: () => void }) {
  const [i, setI] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [progress, setProgress] = useState(0);
  const startRef = useRef<number>(Date.now());

  const slide = SLIDES[i];
  const total = SLIDES.length;

  useEffect(() => {
    if (!playing) return;
    startRef.current = Date.now();
    setProgress(0);
    const tick = setInterval(() => {
      const elapsed = Date.now() - startRef.current;
      const p = Math.min(1, elapsed / SLIDE_MS);
      setProgress(p);
      if (p >= 1) {
        setI((cur) => (cur + 1 < total ? cur + 1 : cur));
        if (i + 1 >= total) setPlaying(false);
      }
    }, 120);
    return () => clearInterval(tick);
  }, [i, playing, total]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "ArrowRight") setI((c) => Math.min(total - 1, c + 1));
      else if (e.key === "ArrowLeft") setI((c) => Math.max(0, c - 1));
      else if (e.key === " ") {
        e.preventDefault();
        setPlaying((p) => !p);
      } else if (e.key === "Escape") onClose?.();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [total, onClose]);

  const overallProgress = useMemo(
    () => (i + progress) / total,
    [i, progress, total],
  );

  return (
    <div className="relative overflow-hidden rounded-2xl border border-border bg-[#0b1e3a] text-white shadow-soft">
      <style>{keyframesCss}</style>
      {/* Stage */}
      <div className="relative aspect-video w-full">
        <StageBackground accent={slide.accent} />
        <div className="absolute inset-0 flex flex-col justify-between p-6 md:p-10">
          <div className="flex items-start justify-between gap-4">
            <div className="max-w-[70%]">
              <p className="text-[10px] uppercase tracking-[0.3em] text-[#c8a24b]">
                {slide.eyebrow}
              </p>
              <h3 className="mt-2 font-display text-2xl leading-tight text-white md:text-4xl">
                {slide.title}
              </h3>
            </div>
            {slide.showFlyer && (
              <img
                src={flyerAsset.url}
                alt="Nkadimeng Danny services flyer"
                className="hidden h-40 w-auto rounded-md border border-white/20 object-cover md:block"
              />
            )}
            {onClose && (
              <button
                onClick={onClose}
                aria-label="Close explainer"
                className="rounded-full bg-white/10 p-1.5 hover:bg-white/20 md:hidden"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          <ul className="space-y-2 pb-24 text-sm md:pb-32 md:text-base">
            {slide.points.map((p, idx) => (
              <li
                key={idx}
                className="flex items-start gap-2 text-white/90"
                style={{
                  animation: `slideInPoint 500ms both`,
                  animationDelay: `${300 + idx * 250}ms`,
                }}
              >
                <span className="mt-2 h-1.5 w-1.5 flex-none rounded-full bg-[#c8a24b]" />
                <span>{p}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Hosts */}
        <div className="pointer-events-none absolute bottom-3 left-4 right-4 flex items-end justify-between">
          <Host name="Kabelo" speaking={slide.host === "kabelo" || slide.host === "both"} skin="#5a3c1a" jacket="#0b1e3a" tie="#c8a24b" />
          <Host name="Naledi" speaking={slide.host === "naledi" || slide.host === "both"} skin="#8b5a2b" jacket="#c8a24b" tie="#0b1e3a" mirror />
        </div>
      </div>

      {/* Progress + controls */}
      <div className="relative bg-black/40 px-4 py-3">
        <div className="h-1 w-full overflow-hidden rounded-full bg-white/10">
          <div
            className="h-full bg-[#c8a24b] transition-[width] duration-100"
            style={{ width: `${overallProgress * 100}%` }}
          />
        </div>
        <div className="mt-2 flex items-center justify-between gap-3 text-xs text-white/80">
          <span>
            Slide {i + 1} / {total} · ~5 min total
          </span>
          <div className="flex items-center gap-1">
            <Button
              size="sm"
              variant="ghost"
              className="text-white hover:bg-white/10"
              onClick={() => setI((c) => Math.max(0, c - 1))}
            >
              <SkipBack className="h-4 w-4" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="text-white hover:bg-white/10"
              onClick={() => setPlaying((p) => !p)}
            >
              {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="text-white hover:bg-white/10"
              onClick={() => setI((c) => Math.min(total - 1, c + 1))}
            >
              <SkipForward className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function StageBackground({ accent }: { accent?: Slide["accent"] }) {
  const grad =
    accent === "gold"
      ? "linear-gradient(140deg, #0b1e3a 0%, #1a2f56 45%, #c8a24b 130%)"
      : accent === "cream"
        ? "linear-gradient(140deg, #0b1e3a 0%, #1a2f56 55%, #f6efe1 140%)"
        : "linear-gradient(140deg, #0b1e3a 0%, #142a4e 60%, #0b1e3a 100%)";
  return (
    <>
      <div className="absolute inset-0" style={{ background: grad }} />
      <div className="absolute -left-16 -top-16 h-64 w-64 rounded-full bg-[#c8a24b]/20 blur-3xl" />
      <div className="absolute -right-20 top-1/3 h-72 w-72 rounded-full bg-white/5 blur-3xl" />
      {/* Registration badge */}
      <div className="absolute right-4 top-4 hidden rounded-md border border-white/15 bg-black/30 px-3 py-1 text-[10px] uppercase tracking-widest text-[#c8a24b] md:block">
        Reg No. 2026/518678/07
      </div>
    </>
  );
}

function Host({
  name,
  speaking,
  skin,
  jacket,
  tie,
  mirror,
}: {
  name: string;
  speaking: boolean;
  skin: string;
  jacket: string;
  tie: string;
  mirror?: boolean;
}) {
  return (
    <div
      className="flex flex-col items-center"
      style={{ transform: mirror ? "scaleX(-1)" : undefined }}
    >
      <svg viewBox="0 0 120 160" className="h-32 w-24 md:h-40 md:w-28">
        {/* Body */}
        <path d="M10 160 Q10 100 60 100 Q110 100 110 160 Z" fill={jacket} />
        <path d="M60 100 L52 155 L60 160 L68 155 Z" fill={tie} />
        <circle cx="60" cy="80" r="22" fill={skin} />
        {/* Hair */}
        <path d="M38 76 Q38 55 60 55 Q82 55 82 76 Q82 63 60 63 Q38 63 38 76 Z" fill="#111" />
        {/* Eyes */}
        <circle cx="52" cy="80" r="2.2" fill="#111" style={{ animation: "blink 6s infinite" }} />
        <circle cx="68" cy="80" r="2.2" fill="#111" style={{ animation: "blink 6s infinite" }} />
        {/* Mouth */}
        <ellipse
          cx="60"
          cy="90"
          rx="6"
          ry={speaking ? 2.5 : 0.8}
          fill="#3a1e1e"
          style={{
            animation: speaking ? "talk 340ms infinite" : undefined,
            transformOrigin: "60px 90px",
          }}
        />
      </svg>
      <span
        className="mt-1 rounded-full bg-black/40 px-2 py-0.5 text-[10px] uppercase tracking-widest text-white/70"
        style={{ transform: mirror ? "scaleX(-1)" : undefined }}
      >
        {name}
      </span>
    </div>
  );
}

const keyframesCss = `
@keyframes talk {
  0%,100% { transform: scaleY(0.6); }
  50% { transform: scaleY(1.4); }
}
@keyframes blink {
  0%,92%,100% { transform: scaleY(1); }
  95% { transform: scaleY(0.1); }
}
@keyframes slideInPoint {
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
}
@media (prefers-reduced-motion: reduce) {
  [style*="animation"] { animation: none !important; }
}
`;
